import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './WarrantyVerification.css';

function WarrantyVerification() {
  const [searchParams] = useSearchParams();
  const [uuid, setUuid] = useState(searchParams.get('uuid') || '');
  const [warranty, setWarranty] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const uuidFromUrl = searchParams.get('uuid');
    if (uuidFromUrl) {
      setUuid(uuidFromUrl);
      verifyWarranty(uuidFromUrl);
    }
  }, [searchParams]);

  const verifyWarranty = async (warrantyUuid) => {
    if (!warrantyUuid || warrantyUuid.trim().length === 0) {
      setError('Please enter a warranty UUID');
      return;
    }

    setLoading(true);
    setError('');
    setWarranty(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/verify-warranty/${warrantyUuid.trim()}`
      );

      const data = await response.json();

      if (!response.ok || !data.valid) {
        setError(data.message || 'Warranty not found');
        return;
      }

      setWarranty(data.warranty);
    } catch (err) {
      console.error('Error verifying warranty:', err);
      setError('Failed to verify warranty. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    verifyWarranty(uuid);
  };

  const getStatusColor = (status) => {
    return status === 'active' ? '#059669' : '#dc2626';
  };

  const getStatusIcon = (status) => {
    return status === 'active' ? '✓' : '✗';
  };

  return (
    <div className="warranty-verification-container">
      <div className="verification-header">
        <h1>🛡️ Vérification de Garantie</h1>
        <p>Vérifiez l'authenticité et le statut de votre garantie produit</p>
      </div>

      <div className="verification-search">
        <form onSubmit={handleSubmit}>
          <div className="search-group">
            <label htmlFor="uuid">UUID de Garantie</label>
            <input
              id="uuid"
              type="text"
              value={uuid}
              onChange={(e) => setUuid(e.target.value)}
              placeholder="Entrez l'UUID de garantie ou scannez le code QR"
              className="uuid-input"
            />
          </div>
          <button type="submit" disabled={loading} className="btn-verify">
            {loading ? 'Vérification...' : 'Vérifier la Garantie'}
          </button>
        </form>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {warranty && (
        <div className="warranty-result">
          <div
            className="warranty-status-banner"
            style={{ backgroundColor: getStatusColor(warranty.status) }}
          >
            <span className="status-icon">{getStatusIcon(warranty.status)}</span>
            <span className="status-text">
              Garantie {warranty.status.toUpperCase()}
            </span>
          </div>

          <div className="warranty-details">
            <section className="detail-section">
              <h2>Informations de Garantie</h2>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">UUID:</span>
                  <span className="detail-value uuid-text">{warranty.uuid}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Statut:</span>
                  <span
                    className="detail-value status-badge"
                    style={{ color: getStatusColor(warranty.status) }}
                  >
                    {warranty.status === 'active' ? '🟢 Active' : '🔴 Expirée'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Date de début:</span>
                  <span className="detail-value">{warranty.start_date}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Date de fin:</span>
                  <span className="detail-value">{warranty.end_date}</span>
                </div>
                {warranty.is_active && (
                  <div className="detail-item">
                    <span className="detail-label">Jours restants:</span>
                    <span className="detail-value highlight">
                      {warranty.days_remaining} jours
                    </span>
                  </div>
                )}
                {warranty.extended && (
                  <div className="detail-item">
                    <span className="detail-label">Prolongée:</span>
                    <span className="detail-value">Oui</span>
                  </div>
                )}
              </div>
            </section>

            <section className="detail-section">
              <h2>Informations Produit</h2>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Nom du produit:</span>
                  <span className="detail-value product-name">
                    {warranty.product.name}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">SKU:</span>
                  <span className="detail-value">{warranty.product.sku}</span>
                </div>
                {warranty.product.category && (
                  <div className="detail-item">
                    <span className="detail-label">Catégorie:</span>
                    <span className="detail-value">{warranty.product.category}</span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Durée de garantie:</span>
                  <span className="detail-value">
                    {warranty.product.warranty_duration_months} mois
                  </span>
                </div>
              </div>
            </section>

            <section className="detail-section">
              <h2>Informations d'Achat</h2>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Numéro de facture:</span>
                  <span className="detail-value">#{warranty.sale.invoice_uuid}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Date de vente:</span>
                  <span className="detail-value">{warranty.sale.sale_date}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Total de vente:</span>
                  <span className="detail-value">
                    {parseFloat(warranty.sale.total).toFixed(2)} DH
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Source:</span>
                  <span className="detail-value">{warranty.sale.source}</span>
                </div>
              </div>
            </section>

            {warranty.client && (
              <section className="detail-section">
                <h2>Informations Client</h2>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Nom:</span>
                    <span className="detail-value">{warranty.client.name}</span>
                  </div>
                  {warranty.client.email && (
                    <div className="detail-item">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{warranty.client.email}</span>
                    </div>
                  )}
                  {warranty.client.phone && (
                    <div className="detail-item">
                      <span className="detail-label">Téléphone:</span>
                      <span className="detail-value">{warranty.client.phone}</span>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>

          {!warranty.is_active && (
            <div className="expired-notice">
              <h3>⚠️ Garantie Expirée</h3>
              <p>
                Cette garantie a expiré le {warranty.end_date}. Veuillez contacter le service client
                pour les options de prolongation de garantie.
              </p>
            </div>
          )}
        </div>
      )}

      {!warranty && !loading && !error && (
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <p>Entrez un UUID de garantie pour vérifier son authenticité et son statut</p>
        </div>
      )}
    </div>
  );
}

export default WarrantyVerification;
